/**
 * „Something in the database changed" signal for the admin row counters.
 *
 * The admin sidebar shows a row count per table (GET /api/admin/data/), loaded
 * once — so creating or deleting a user, web, template… left every badge stale
 * until a full reload. Instead of wiring each admin page, every successful
 * write to OUR API is reported here (plugins/admin-counts.client.ts wraps
 * window.fetch, api/client.ts has a middleware); the sidebar and /admin/data
 * watch `adminCountsVersion` and re-read the index.
 *
 * Only writes made while an admin screen is open count (`/admin…` route), so
 * editor sessions never trigger the 40-table COUNT query. Third-party POSTs
 * (Sentry envelopes are `…/api/<project>/envelope/`) never count — the origin
 * must be the API's. Bursts (the webs page deletes a selection one request at
 * a time) collapse into one bump.
 */
import { ref } from 'vue'
import { getApiBase } from '~/utils/api-base'

export const adminCountsVersion = ref(0)

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])
let timer: ReturnType<typeof setTimeout> | null = null

export function bumpAdminCounts() {
  if (timer) clearTimeout(timer)
  timer = setTimeout(() => {
    timer = null
    adminCountsVersion.value++
  }, 300)
}

function isOwnApi(url: string): boolean {
  try {
    const u = new URL(url, window.location.href)
    return u.origin === new URL(getApiBase(), window.location.href).origin && u.pathname.startsWith('/api/')
  } catch {
    return false
  }
}

/** Called for every finished request; bumps on a successful write to our API on an admin screen. */
export function noteRequest(method: string | undefined, url: string, ok: boolean) {
  if (!ok || typeof window === 'undefined') return
  if (!WRITE_METHODS.has((method || 'GET').toUpperCase())) return
  if (!window.location.pathname.startsWith('/admin')) return
  if (!isOwnApi(url)) return
  bumpAdminCounts()
}
